import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { TableCell, TableHead } from "@/components/ui/table";
import { useSendReceipt } from "@/hooks/useSendReceipt";
import { canSendReceipt } from "@/lib/order-utils";
import { cn, formatCurrency } from "@/lib/utils";
import type {
  QueryParams,
  SendReceiptModalProps,
} from "@/types/order-history/order-history-types";
import { useOrderHistory, useOrderHistoryPOS } from "api/hooks/useOrderHistory";
import type { OrderResponseDto, PosOrderDto } from "api/models";
import { ProviderEnum } from "api/models";
import { format } from "date-fns";
import { ChevronDown, Loader2, Mail, Send } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { DateCell, OrdersTable, PaymentCell, StatusCell } from "./OrdersTable";

const HEAD_CLASS =
  "min-w-[100px] sm:min-w-[120px] py-4 px-4 text-sm font-semibold text-foreground";
const CELL_CLASS = "py-4 px-4 text-sm";

function createGetSearchableText<T>(
  getFields: (order: T) => (string | null | undefined)[]
) {
  return (order: T) =>
    getFields(order)
      .filter((field): field is string => Boolean(field))
      .join(" ");
}

function SendReceiptModal({
  isOpen,
  onClose,
  orderNumber,
  onSend,
  isLoading = false,
}: SendReceiptModalProps) {
  const [email, setEmail] = useState("");

  const handleSend = async () => {
    if (!email) return;
    await onSend(email);
    setEmail("");
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="flex items-center gap-3 mb-2">
            <div className="p-3 rounded-full bg-blue-100 dark:bg-blue-900">
              <Mail className="h-6 w-6 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <DialogTitle className="text-xl">Send Email Receipt</DialogTitle>
              <DialogDescription className="text-sm">
                Order {orderNumber}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="email" className="text-sm font-medium">
              Email Address
            </Label>
            <Input
              id="email"
              type="email"
              placeholder="customer@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoFocus
              disabled={isLoading}
            />
          </div>
          <div className="p-3 rounded-lg bg-muted/50 text-sm text-muted-foreground">
            <p>The receipt will include:</p>
            <ul className="mt-2 space-y-1 ml-4 list-disc">
              <li>Order details and items</li>
              <li>Payment information</li>
              <li>Total amount and taxes</li>
            </ul>
          </div>
        </div>
        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={onClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button onClick={handleSend} disabled={!email || isLoading}>
            {isLoading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Sending...
              </>
            ) : (
              <>
                <Send className="h-4 w-4 mr-2" />
                Send Receipt
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function useReceiptModal<
  T extends { orderNumber: string },
  TArgs extends unknown[]
>(
  sendReceipt: (
    orderId: string,
    email: string,
    ...args: TArgs
  ) => Promise<unknown>,
  getOrderId: (order: T) => string,
  getOrderArgs: (order: T) => TArgs
) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<T | null>(null);

  const handleOpenModal = (order: T) => {
    setSelectedOrder(order);
    setIsModalOpen(true);
  };

  const handleSendReceipt = async (email: string) => {
    if (!selectedOrder) return;
    await sendReceipt(
      getOrderId(selectedOrder),
      email,
      ...getOrderArgs(selectedOrder)
    );
  };

  return {
    isModalOpen,
    selectedOrder,
    handleOpenModal,
    handleSendReceipt,
    closeModal: () => setIsModalOpen(false),
  };
}

export function PosOrdersTab({
  queryParams,
  currentPage,
  onPageChange,
  searchQuery,
}: {
  queryParams: QueryParams;
  currentPage: number;
  onPageChange: (page: number) => void;
  searchQuery?: string;
}) {
  const { data, isLoading } = useOrderHistoryPOS(
    queryParams,
    queryParams.businessIds.length === 1
  );
  const { sendPosReceipt, isLoading: isSending } = useSendReceipt();
  const {
    isModalOpen,
    selectedOrder,
    handleOpenModal,
    handleSendReceipt,
    closeModal,
  } = useReceiptModal<PosOrderDto, [string]>(
    sendPosReceipt,
    (order) => order.id,
    (order) => [order.businessId || ""]
  );

  const getSearchableText = createGetSearchableText<PosOrderDto>((order) => [
    order.orderNumber,
    order.business?.name,
    order.customer?.first_name,
    order.customer?.last_name,
    order.customer?.phone,
  ]);

  const renderExpandedContent = (order: PosOrderDto) => {
    const taxSummary = order.taxSummary;
    const canSend = canSendReceipt(ProviderEnum.MunchiPos);

    return (
      <div className="space-y-4">
        <div className="flex flex-wrap gap-4 p-4 rounded-lg bg-muted/20 border">
          <div className="space-y-1">
            <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
              Order #
            </div>
            <div className="text-base font-semibold">{order.orderNumber}</div>
          </div>
          <div className="space-y-1">
            <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
              Type
            </div>
            <div className="text-sm font-medium">{order.orderType}</div>
          </div>
          <div className="space-y-1">
            <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
              Format
            </div>
            <div className="text-sm font-medium">{order.orderFormat}</div>
          </div>
          {order.spotNumber && (
            <div className="space-y-1">
              <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                Spot
              </div>
              <div className="text-sm font-medium">{order.spotNumber}</div>
            </div>
          )}
        </div>
        {order.customer && (
          <Collapsible defaultOpen className="group">
            <Card className="border overflow-hidden">
              <CollapsibleTrigger asChild>
                <button
                  type="button"
                  className="w-full flex items-center justify-between p-4 text-left hover:bg-muted/30 transition-colors"
                >
                  <h4 className="text-sm font-semibold text-foreground">
                    Customer
                  </h4>
                  <ChevronDown className="h-4 w-4 shrink-0 transition-transform duration-200 group-data-[state=open]:rotate-180" />
                </button>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <CardContent className="pt-0 pb-4 px-4">
                  <div className="space-y-2">
                    <div className="text-base font-medium">
                      {`${order.customer.first_name} ${order.customer.last_name}`}
                    </div>
                    {order.customer.phone && (
                      <div className="text-sm text-muted-foreground">
                        {order.customer.phone}
                      </div>
                    )}
                  </div>
                </CardContent>
              </CollapsibleContent>
            </Card>
          </Collapsible>
        )}
        {taxSummary?.taxSummaries && (
          <Collapsible defaultOpen className="group">
            <Card className="border overflow-hidden">
              <CollapsibleTrigger asChild>
                <button
                  type="button"
                  className="w-full flex items-center justify-between p-4 text-left hover:bg-muted/30 transition-colors"
                >
                  <h4 className="text-sm font-semibold text-foreground">
                    Tax Breakdown
                  </h4>
                  <ChevronDown className="h-4 w-4 shrink-0 transition-transform duration-200 group-data-[state=open]:rotate-180" />
                </button>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <CardContent className="pt-0 p-5">
                  <div className="space-y-4">
                    {taxSummary.taxSummaries.map((tax, idx) => (
                      <div
                        key={`${tax.taxRate}-${idx}`}
                        className="grid grid-cols-3 gap-6 pb-4 border-b last:border-0 last:pb-0"
                      >
                        <div className="space-y-1">
                          <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                            Tax Rate
                          </div>
                          <div className="text-sm font-medium">
                            {tax.taxRate}%
                          </div>
                        </div>
                        <div className="space-y-1">
                          <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                            Without Tax
                          </div>
                          <div className="text-sm font-medium">
                            {formatCurrency(tax.totalWithoutTax / 100)}
                          </div>
                        </div>
                        <div className="space-y-1">
                          <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                            Tax Amount
                          </div>
                          <div className="text-sm font-medium">
                            {formatCurrency(tax.totalTax / 100)}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </CollapsibleContent>
            </Card>
          </Collapsible>
        )}
        {order.items && order.items.length > 0 && (
          <Collapsible defaultOpen className="group">
            <Card className="border overflow-hidden">
              <CollapsibleTrigger asChild>
                <button
                  type="button"
                  className="w-full flex items-center justify-between p-4 text-left hover:bg-muted/30 transition-colors"
                >
                  <h4 className="text-sm font-semibold text-foreground">
                    Items ({order.items.length})
                  </h4>
                  <ChevronDown className="h-4 w-4 shrink-0 transition-transform duration-200 group-data-[state=open]:rotate-180" />
                </button>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <CardContent className="pt-0 p-5">
                  <div className="space-y-4">
                    {order.items.map((item) => (
                      <div
                        key={item.id}
                        className="border-l-2 border-primary/20 pl-4 space-y-2 py-2"
                      >
                        <div className="flex justify-between items-start gap-4">
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-sm">
                              <span className="text-muted-foreground mr-2">
                                {item.quantity}x
                              </span>
                              {item.name}
                            </div>
                            {item.comments && (
                              <div className="text-xs text-muted-foreground mt-1 italic">
                                Note: {item.comments}
                              </div>
                            )}
                          </div>
                          <div className="font-semibold text-sm shrink-0">
                            {formatCurrency(item.itemPrice.total.amount / 100)}
                          </div>
                        </div>
                        {item.options && item.options.length > 0 && (
                          <div className="pl-6 space-y-1 mt-2">
                            {item.options.map((option) => (
                              <div
                                key={option.id}
                                className="text-xs text-muted-foreground flex items-center gap-1"
                              >
                                <span className="text-primary">•</span>
                                {option.name}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </CollapsibleContent>
            </Card>
          </Collapsible>
        )}
        {order.discounts && order.discounts.length > 0 && (
          <Collapsible defaultOpen className="group">
            <Card className="border border-green-200 dark:border-green-900 overflow-hidden">
              <CollapsibleTrigger asChild>
                <button
                  type="button"
                  className="w-full flex items-center justify-between p-4 text-left hover:bg-muted/30 transition-colors"
                >
                  <h4 className="text-sm font-semibold text-foreground">
                    Discounts ({order.discounts.length})
                  </h4>
                  <ChevronDown className="h-4 w-4 shrink-0 transition-transform duration-200 group-data-[state=open]:rotate-180" />
                </button>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <CardContent className="pt-0 p-5">
                  <div className="space-y-3">
                    {order.discounts.map((discount, idx) => (
                      <div
                        key={discount.name || `discount-${idx}`}
                        className="flex justify-between items-center text-sm"
                      >
                        <span className="font-medium">{discount.name}</span>
                        <span className="font-semibold text-green-600 dark:text-green-400">
                          {formatCurrency(discount.appliedAmount.amount / 100)}
                        </span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </CollapsibleContent>
            </Card>
          </Collapsible>
        )}
        {order.paymentEvents && order.paymentEvents.length > 0 && (
          <Collapsible defaultOpen className="group">
            <Card className="border overflow-hidden">
              <CollapsibleTrigger asChild>
                <button
                  type="button"
                  className="w-full flex items-center justify-between p-4 text-left hover:bg-muted/30 transition-colors"
                >
                  <h4 className="text-sm font-semibold text-foreground">
                    Payment Details ({order.paymentEvents.length})
                  </h4>
                  <ChevronDown className="h-4 w-4 shrink-0 transition-transform duration-200 group-data-[state=open]:rotate-180" />
                </button>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <CardContent className="pt-0 p-5">
                  <div className="space-y-4">
                    {order.paymentEvents.map((event) => (
                      <div
                        key={event.id}
                        className="grid grid-cols-2 md:grid-cols-3 gap-6 pb-4 border-b last:border-0 last:pb-0"
                      >
                        <div className="space-y-1">
                          <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                            Type
                          </div>
                          <div className="text-sm font-medium">
                            {event.type}
                          </div>
                        </div>
                        <div className="space-y-1">
                          <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                            Provider
                          </div>
                          <div className="text-sm font-medium">
                            {event.provider}
                          </div>
                        </div>
                        <div className="space-y-1">
                          <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                            Amount
                          </div>
                          <div className="text-sm font-medium">
                            {formatCurrency(event.amount / 100)}
                          </div>
                        </div>
                        {event.cardDetail && (
                          <>
                            <div className="space-y-1">
                              <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                                Card Type
                              </div>
                              <div className="text-sm font-medium">
                                {event.cardDetail.cardType}
                              </div>
                            </div>
                            <div className="space-y-1">
                              <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                                Card Number
                              </div>
                              <div className="text-sm font-medium">
                                <span className="font-mono">
                                  {event.cardDetail.cardNumber}
                                </span>
                              </div>
                            </div>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </CollapsibleContent>
            </Card>
          </Collapsible>
        )}
        {order.refunds && order.refunds.length > 0 && (
          <Collapsible defaultOpen className="group">
            <Card className="border border-red-200 dark:border-red-900 overflow-hidden">
              <CollapsibleTrigger asChild>
                <button
                  type="button"
                  className="w-full flex items-center justify-between p-4 text-left hover:bg-muted/30 transition-colors"
                >
                  <h4 className="text-sm font-semibold text-foreground">
                    Refunds ({order.refunds.length})
                  </h4>
                  <ChevronDown className="h-4 w-4 shrink-0 transition-transform duration-200 group-data-[state=open]:rotate-180" />
                </button>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <CardContent className="pt-0 p-5">
                  <div className="space-y-3">
                    {order.refunds.map((refund) => (
                      <div
                        key={refund.id}
                        className="flex justify-between items-center text-sm"
                      >
                        <div className="flex items-center gap-3">
                          <Badge variant="secondary">{refund.status}</Badge>
                          <span className="text-xs text-muted-foreground">
                            {format(new Date(refund.processedAt), "PPp")}
                          </span>
                        </div>
                        <span className="font-semibold text-red-600 dark:text-red-400">
                          -{formatCurrency(refund.amount / 100)}
                        </span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </CollapsibleContent>
            </Card>
          </Collapsible>
        )}
        {canSend && (
          <div className="pt-4 border-t flex justify-end">
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleOpenModal(order)}
              className="h-9"
            >
              <Mail className="h-4 w-4 mr-2" />
              Send Email Receipt
            </Button>
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <OrdersTable
        orders={data?.orders || []}
        pagination={
          data?.pagination
            ? {
                page: data.pagination.page,
                totalPages: data.pagination.totalPages,
              }
            : { page: 1, totalPages: 1 }
        }
        isLoading={isLoading}
        currentPage={currentPage}
        onPageChange={onPageChange}
        getOrderId={(order) => order.id}
        onSendReceipt={handleOpenModal}
        canSendReceipt={() => canSendReceipt(ProviderEnum.MunchiPos)}
        searchQuery={searchQuery}
        getSearchableText={getSearchableText}
        renderTableHeaders={() => (
          <>
            <TableHead className={cn(HEAD_CLASS)}>Order #</TableHead>
            <TableHead
              className={cn("min-w-[130px] sm:min-w-[160px]", HEAD_CLASS)}
            >
              Date
            </TableHead>
            <TableHead
              className={cn("min-w-[110px] sm:min-w-[140px]", HEAD_CLASS)}
            >
              Business
            </TableHead>
            <TableHead
              className={cn("min-w-[100px] sm:min-w-[120px]", HEAD_CLASS)}
            >
              Status
            </TableHead>
            <TableHead className={cn(HEAD_CLASS)}>Payment</TableHead>
            <TableHead className={cn(HEAD_CLASS)}>Total</TableHead>
          </>
        )}
        renderTableRow={(order: PosOrderDto) => {
          const taxSummary = order.taxSummary;
          const total = (taxSummary?.grandTotal || 0) / 100;
          const payment = order.paymentEvents?.[0]?.type || "N/A";

          return (
            <>
              <TableCell
                className={cn(
                  "font-mono font-semibold text-foreground",
                  CELL_CLASS
                )}
              >
                {order.orderNumber}
              </TableCell>
              <TableCell className={CELL_CLASS}>
                <DateCell date={order.createdAt} />
              </TableCell>
              <TableCell className={cn("text-foreground", CELL_CLASS)}>
                {order.business?.name || "—"}
              </TableCell>
              <TableCell className={CELL_CLASS}>
                <StatusCell status={order.status} />
              </TableCell>
              <TableCell className={CELL_CLASS}>
                <PaymentCell payment={payment} />
              </TableCell>
              <TableCell
                className={cn(
                  "font-semibold tabular-nums text-foreground",
                  CELL_CLASS
                )}
              >
                {formatCurrency(total)}
              </TableCell>
            </>
          );
        }}
        renderExpandedContent={renderExpandedContent}
      />
      {selectedOrder && (
        <SendReceiptModal
          isOpen={isModalOpen}
          onClose={closeModal}
          orderNumber={selectedOrder.orderNumber}
          onSend={handleSendReceipt}
          isLoading={isSending}
        />
      )}
    </>
  );
}

export function OnlineOrdersTab({
  queryParams,
  currentPage,
  onPageChange,
  searchQuery,
}: {
  queryParams: QueryParams;
  currentPage: number;
  onPageChange: (page: number) => void;
  searchQuery?: string;
}) {
  const { data, isLoading } = useOrderHistory(
    queryParams,
    queryParams.businessIds.length > 0
  );
  const { sendOnlineReceipt, sendKioskReceipt, isLoading: isSending } =
    useSendReceipt();
  const sendReceiptForOrder = async (
    orderId: string,
    email: string,
    provider: ProviderEnum,
    orderingBusinessId?: number
  ) => {
    if (orderingBusinessId == null) {
      toast.error("This order is missing its business ID");
      return Promise.reject(
        new Error("Receipt cannot be sent without orderingBusinessId")
      );
    }

    if (
      provider === ProviderEnum.MunchiKiosk ||
      provider === ProviderEnum.Kiosk
    ) {
      return sendKioskReceipt(
        orderId,
        email,
        String(orderingBusinessId),
        provider
      );
    }

    return sendOnlineReceipt(
      orderId,
      email,
      provider,
      String(orderingBusinessId)
    );
  };
  const {
    isModalOpen,
    selectedOrder,
    handleOpenModal,
    handleSendReceipt,
    closeModal,
  } = useReceiptModal<OrderResponseDto, [ProviderEnum, number | undefined]>(
    sendReceiptForOrder,
    (order) => order.externalId,
    (order) => [order.provider, order.orderingBusinessId]
  );

  const showBusinessBadge = queryParams.businessIds.length > 1;

  const getSearchableText = createGetSearchableText<OrderResponseDto>(
    (order) => [
      order.orderNumber,
      order.provider,
      order.customer?.name,
      order.customer?.email,
      order.customer?.phone,
    ]
  );

  const renderExpandedContent = (order: OrderResponseDto) => {
    const canSend = canSendReceipt(order.provider);

    return (
      <div className="space-y-4">
        <div className="flex flex-wrap gap-4 p-4 rounded-lg bg-muted/20 border">
          <div className="space-y-1">
            <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
              Order #
            </div>
            <div className="text-base font-semibold">{order.orderNumber}</div>
          </div>
          <div className="space-y-1">
            <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
              Provider
            </div>
            <div className="text-sm font-medium">{order.provider || "—"}</div>
          </div>
          <div className="space-y-1">
            <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
              Type
            </div>
            <div className="text-sm font-medium">{order.orderType}</div>
          </div>
          <div className="space-y-1">
            <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
              Payment
            </div>
            <div className="text-sm font-medium">
              <Badge variant="secondary">{order.paymentStatus}</Badge>
            </div>
          </div>
        </div>
        {order.customer && (
          <Collapsible defaultOpen className="group">
            <Card className="border overflow-hidden">
              <CollapsibleTrigger asChild>
                <button
                  type="button"
                  className="w-full flex items-center justify-between p-4 text-left hover:bg-muted/30 transition-colors"
                >
                  <h4 className="text-sm font-semibold text-foreground">
                    Customer
                  </h4>
                  <ChevronDown className="h-4 w-4 shrink-0 transition-transform duration-200 group-data-[state=open]:rotate-180" />
                </button>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <CardContent className="pt-0 pb-4 px-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="space-y-1">
                      <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                        Name
                      </div>
                      <div className="text-sm font-medium">
                        {order.customer.name}
                      </div>
                    </div>
                    {order.customer.email && (
                      <div className="space-y-1">
                        <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                          Email
                        </div>
                        <div className="text-sm font-medium">
                          <span className="break-all">
                            {order.customer.email}
                          </span>
                        </div>
                      </div>
                    )}
                    {order.customer.phone && (
                      <div className="space-y-1">
                        <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                          Phone
                        </div>
                        <div className="text-sm font-medium">
                          {order.customer.phone}
                        </div>
                      </div>
                    )}
                  </div>
                </CardContent>
              </CollapsibleContent>
            </Card>
          </Collapsible>
        )}
        {order.delivery?.location && (
          <Collapsible defaultOpen className="group">
            <Card className="border overflow-hidden">
              <CollapsibleTrigger asChild>
                <button
                  type="button"
                  className="w-full flex items-center justify-between p-4 text-left hover:bg-muted/30 transition-colors"
                >
                  <h4 className="text-sm font-semibold text-foreground">
                    Delivery Address
                  </h4>
                  <ChevronDown className="h-4 w-4 shrink-0 transition-transform duration-200 group-data-[state=open]:rotate-180" />
                </button>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <CardContent className="pt-0 p-5">
                  <div className="text-sm font-medium">
                    {order.delivery.location.formatted_address}
                  </div>
                </CardContent>
              </CollapsibleContent>
            </Card>
          </Collapsible>
        )}
        {order.products && order.products.length > 0 && (
          <Collapsible defaultOpen className="group">
            <Card className="border overflow-hidden">
              <CollapsibleTrigger asChild>
                <button
                  type="button"
                  className="w-full flex items-center justify-between p-4 text-left hover:bg-muted/30 transition-colors"
                >
                  <h4 className="text-sm font-semibold text-foreground">
                    Items ({order.products.length})
                  </h4>
                  <ChevronDown className="h-4 w-4 shrink-0 transition-transform duration-200 group-data-[state=open]:rotate-180" />
                </button>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <CardContent className="pt-0 p-5">
                  <div className="space-y-4">
                    {order.products.map((product) => (
                      <div
                        key={product.id}
                        className="border-l-2 border-primary/20 pl-4 space-y-2 py-2"
                      >
                        <div className="flex justify-between items-start gap-4">
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-sm">
                              <span className="text-muted-foreground mr-2">
                                {product.quantity}x
                              </span>
                              {product.name}
                            </div>
                            {product.comment && (
                              <div className="text-xs text-muted-foreground mt-1 italic">
                                Note: {product.comment}
                              </div>
                            )}
                          </div>
                          <div className="font-semibold text-sm shrink-0">
                            {formatCurrency(Number(product.paidPrice))}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </CollapsibleContent>
            </Card>
          </Collapsible>
        )}
        {order.discounts && order.discounts.length > 0 && (
          <Collapsible defaultOpen className="group">
            <Card className="border border-green-200 dark:border-green-900 overflow-hidden">
              <CollapsibleTrigger asChild>
                <button
                  type="button"
                  className="w-full flex items-center justify-between p-4 text-left hover:bg-muted/30 transition-colors"
                >
                  <h4 className="text-sm font-semibold text-foreground">
                    Discounts ({order.discounts.length})
                  </h4>
                  <ChevronDown className="h-4 w-4 shrink-0 transition-transform duration-200 group-data-[state=open]:rotate-180" />
                </button>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <CardContent className="pt-0 p-5">
                  <div className="space-y-3">
                    {order.discounts.map((discount) => (
                      <div
                        key={discount.id}
                        className="flex justify-between items-center text-sm"
                      >
                        <span className="font-medium">
                          {discount.discountName}
                        </span>
                        <span className="font-semibold text-green-600 dark:text-green-400">
                          -{formatCurrency(Number(discount.discountAmount))}
                        </span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </CollapsibleContent>
            </Card>
          </Collapsible>
        )}
        {order.refunds && order.refunds.length > 0 && (
          <Collapsible defaultOpen className="group">
            <Card className="border border-red-200 dark:border-red-900 overflow-hidden">
              <CollapsibleTrigger asChild>
                <button
                  type="button"
                  className="w-full flex items-center justify-between p-4 text-left hover:bg-muted/30 transition-colors"
                >
                  <h4 className="text-sm font-semibold text-foreground">
                    Refunds ({order.refunds.length})
                  </h4>
                  <ChevronDown className="h-4 w-4 shrink-0 transition-transform duration-200 group-data-[state=open]:rotate-180" />
                </button>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <CardContent className="pt-0 p-5">
                  <div className="space-y-3">
                    {order.refunds.map((refund) => (
                      <div
                        key={refund.id}
                        className="flex justify-between items-center text-sm"
                      >
                        <div className="flex items-center gap-3">
                          <Badge variant="secondary">{refund.status}</Badge>
                          <span className="text-xs text-muted-foreground">
                            {format(new Date(refund.processedAt), "PPp")}
                          </span>
                        </div>
                        <span className="font-semibold text-red-600 dark:text-red-400">
                          -{formatCurrency(Number(refund.amount))}
                        </span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </CollapsibleContent>
            </Card>
          </Collapsible>
        )}
        {canSend && (
          <div className="pt-4 border-t flex justify-end">
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleOpenModal(order)}
              className="h-9"
            >
              <Mail className="h-4 w-4 mr-2" />
              Send Email Receipt
            </Button>
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <OrdersTable
        orders={data?.orders || []}
        pagination={
          data?.pagination
            ? {
                page: data.pagination.page,
                totalPages: data.pagination.totalPages,
              }
            : { page: 1, totalPages: 1 }
        }
        isLoading={isLoading}
        currentPage={currentPage}
        onPageChange={onPageChange}
        getOrderId={(order) => order.id}
        onSendReceipt={handleOpenModal}
        canSendReceipt={(order) => canSendReceipt(order.provider)}
        searchQuery={searchQuery}
        getSearchableText={getSearchableText}
        renderTableHeaders={() => (
          <>
            <TableHead className={cn(HEAD_CLASS)}>Order #</TableHead>
            <TableHead
              className={cn("min-w-[140px] sm:min-w-[160px]", HEAD_CLASS)}
            >
              Date
            </TableHead>
            <TableHead
              className={cn("min-w-[90px] sm:min-w-[110px]", HEAD_CLASS)}
            >
              Provider
            </TableHead>
            <TableHead
              className={cn("min-w-[100px] sm:min-w-[120px]", HEAD_CLASS)}
            >
              Status
            </TableHead>
            <TableHead className={cn(HEAD_CLASS)}>Payment</TableHead>
            <TableHead className={cn(HEAD_CLASS)}>Amount</TableHead>
          </>
        )}
        renderTableRow={(order: OrderResponseDto) => {
          const total = Number(order.priceSummary?.totalNet || 0);

          return (
            <>
              <TableCell
                className={cn(
                  "font-mono font-semibold text-foreground",
                  CELL_CLASS
                )}
              >
                {order.orderNumber}
                {showBusinessBadge && order.business?.name && (
                  <Badge
                    variant="secondary"
                    className="mt-1 block w-fit max-w-[160px] truncate font-sans text-[11px] font-normal"
                    title={order.business.name}
                  >
                    {order.business.name}
                  </Badge>
                )}
              </TableCell>
              <TableCell className={CELL_CLASS}>
                <DateCell date={order.createdAt} />
              </TableCell>
              <TableCell className={cn("text-foreground", CELL_CLASS)}>
                {order.provider || "—"}
              </TableCell>
              <TableCell className={CELL_CLASS}>
                <StatusCell status={order.status} />
              </TableCell>
              <TableCell className={CELL_CLASS}>
                <PaymentCell payment={order.paymentType || "N/A"} />
              </TableCell>
              <TableCell
                className={cn(
                  "font-semibold tabular-nums text-foreground",
                  CELL_CLASS
                )}
              >
                {formatCurrency(total)}
              </TableCell>
            </>
          );
        }}
        renderExpandedContent={renderExpandedContent}
      />
      {selectedOrder && (
        <SendReceiptModal
          isOpen={isModalOpen}
          onClose={closeModal}
          orderNumber={selectedOrder.orderNumber}
          onSend={handleSendReceipt}
          isLoading={isSending}
        />
      )}
    </>
  );
}
